/**
 * Amplifier profiles -- group 07.
 *
 * Eight European designs picked so that no two share an output mechanism: a
 * DC-coupled balanced class-AB power amplifier, a rail-tracking linear design,
 * a high-voltage class-A stage, a regulated push-pull pentode integrated, an
 * output-transformerless triode monoblock, a single-ended transformer-coupled
 * headphone stage, a valve-into-MOSFET hybrid and a low-feedback push-pull
 * integrated with deliberately narrow magnetics. Source impedance, feedback
 * factor and bandwidth are the three numbers that actually separate them, so
 * each entry states all three. Most of these manufacturers publish little or no
 * distortion data; where that is the case the entry says so and is tagged
 * ESTIMATED rather than dressed up as a specification.
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeAmp, usd } from '../kit';

export const AMPS_07: AmpProfile[] = [
  makeAmp({
    mfr: 'Burmester',
    model: '911 Mk3',
    price: usd(28000, 'APPROXIMATE', 2020, 'German list price converted and rounded; Burmester pricing varies by market and the figure is not confirmed.'),
    year: 2011,
    country: 'Germany',
    topology: 'solid-state-ab',
    form: 'stereo-power',
    devices: 'Fully balanced X-Amp voltage gain modules, DC-coupled throughout, bipolar output bank',
    power: { watts8: 215, watts4: 350, confidence: 'ESTIMATED' },
    gainDb: 29,
    thdPercent: 0.02,
    snrDb: 110,
    bandwidth: [1, 150000],
    dampingFactor: 350,
    feedbackDb: 26,
    crosstalkDb: -95,
    balancedInput: true,
    dynamics: { sagDb: 0.28, sagTimeMs: 40, recoveryMs: 110, thermalDb: 0.1, slewRateVus: 80, headroomDb: 2.4 },
    clipping: { kind: 'asymmetric', kneeDb: 2.0, stickiness: 0.06 },
    confidence: 'ESTIMATED',
    accent: '#c9ccd1',
    tags: ['dc-coupled', 'balanced', 'x-amp'],
    notes:
      'DC coupling is the design point being modelled: there is no series capacitor anywhere in the path, so the low-frequency corner is set by a servo and by the output devices rather than by an RC network, which is why the modelled response is still flat at 1 Hz. Class-AB bias plus roughly 26 dB of global feedback puts the residue in the crossover region, giving odd-order products that persist at low level instead of the smoothly rising second harmonic of a class-A design. A damping factor near 350 means the loudspeaker impedance curve prints essentially nothing onto the response, which is the practical contrast with the OTL and single-ended entries in this group.',
    provenanceNote:
      'The DC-coupled fully balanced topology and the X-Amp gain module come from Burmester published material. Power, THD, signal-to-noise, bandwidth and damping factor are estimates consistent with the 911 series; Burmester publishes limited numerical data and none of these figures could be confirmed, so the entry is tagged ESTIMATED and nothing is claimed as MANUFACTURER_SPEC.',
  }),

  makeAmp({
    mfr: 'MBL',
    model: 'Corona C21',
    price: usd(10600, 'APPROXIMATE', 2018, 'US retail has been quoted around 10,000-11,000 USD; treated as approximate.'),
    year: 2013,
    country: 'Germany',
    topology: 'class-h',
    form: 'stereo-power',
    devices: 'LASA linear output stage fed from a tracking switch-mode rail',
    power: { watts8: 180, watts4: 300, confidence: 'ESTIMATED' },
    gainDb: 29,
    thdPercent: 0.03,
    snrDb: 105,
    bandwidth: [5, 80000],
    dampingFactor: 250,
    feedbackDb: 30,
    crosstalkDb: -90,
    balancedInput: true,
    nonlinear: { kind: 'class-b-crossover', amount: 0.42, crossoverWidth: 0.0055, headroomDb: 9, asymmetry: 0.18, oversample: 4 },
    dynamics: { sagDb: 0.2, sagTimeMs: 30, recoveryMs: 90, slewRateVus: 55, headroomDb: 3.0 },
    clipping: { kind: 'asymmetric', kneeDb: 1.6, stickiness: 0.07 },
    confidence: 'ESTIMATED',
    accent: '#9fb6cd',
    tags: ['tracking-rails', 'switch-mode-supply', 'high-headroom'],
    notes:
      'A linear output stage whose supply rails follow the signal envelope, so the device dissipation stays low while the audio path itself never switches. The behaviour that matters is that distortion versus level is not monotonic: below the rail-commutation threshold the amplifier behaves like a small class-AB amplifier on a low supply, and each rail transition adds a brief high-order event that a fixed-rail design does not have. Large short-term headroom follows from the same mechanism, which is modelled here as 3 dB of peak capability above the continuous rating with very little supply sag.',
    provenanceNote:
      'MBL describes LASA as a linear output stage supplied from a tracking switch-mode power supply. Modelling it as class-H is an authoring interpretation of that description, not an MBL classification, and is flagged here as such. Power, THD, signal-to-noise, bandwidth and damping factor are estimates; MBL does not publish a full specification set for this model, so the entry is tagged ESTIMATED.',
  }),

  makeAmp({
    mfr: 'T+A elektroakustik',
    model: 'A 3000 HV',
    price: usd(26000, 'APPROXIMATE', 2021, 'European list price converted and rounded; treated as approximate.'),
    year: 2012,
    country: 'Germany',
    topology: 'solid-state-a',
    form: 'stereo-power',
    devices: 'High-voltage driver stages with a large class-A operating window into a bipolar output bank',
    power: { watts8: 200, watts4: 300, confidence: 'ESTIMATED' },
    gainDb: 29,
    thdPercent: 0.01,
    snrDb: 108,
    bandwidth: [1, 180000],
    dampingFactor: 180,
    feedbackDb: 12,
    crosstalkDb: -100,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.38, asymmetry: 0.42, bias: 0.6, headroomDb: 13, oversample: 4 },
    dynamics: { sagDb: 0.3, sagTimeMs: 50, recoveryMs: 130, thermalDb: 0.38, thermalTimeMs: 9000, slewRateVus: 90, headroomDb: 1.9 },
    clipping: { kind: 'asymmetric', kneeDb: 3.6, stickiness: 0.1 },
    confidence: 'ESTIMATED',
    accent: '#6f8fa8',
    tags: ['high-voltage', 'low-feedback', 'class-a-window'],
    notes:
      'The high-voltage concept runs the small-signal and driver stages at valve-like supply voltages so that each device works across a small, near-linear fraction of its transfer curve. The modelled consequence is low open-loop distortion, which in turn permits only about 12 dB of global feedback -- roughly half of what the Burmester profile uses -- so the residue decays fast with order (H2, then H3, little above H4) and grows with level rather than sitting at a fixed floor. Bandwidth extends past 100 kHz because the loop is not being asked to correct much, and the damping factor is deliberately lower than the fully fed-back designs here.',
    provenanceNote:
      'The high-voltage operating concept and the class-A window come from T+A published descriptions of the HV series. Power, THD, signal-to-noise, bandwidth, feedback factor and damping factor are estimates, not quoted figures, so the entry is tagged ESTIMATED. The feedback figure in particular is inferred from the design philosophy rather than published.',
  }),

  makeAmp({
    mfr: 'Octave Audio',
    model: 'V 80 SE',
    price: usd(14000, 'APPROXIMATE', 2019, 'US retail has been quoted in the 13,000-15,000 USD range depending on options; treated as approximate.'),
    year: 2014,
    country: 'Germany',
    topology: 'tube-push-pull',
    form: 'integrated',
    devices: 'KT120 push-pull output with a small-signal double-triode front end, regulated screen supplies and electronic bias management',
    power: { watts8: 120, watts4: 110, confidence: 'ESTIMATED' },
    gainDb: 34,
    thdPercent: 0.15,
    snrDb: 95,
    bandwidth: [10, 50000],
    outputTransformer: true,
    transformerLfHz: 14,
    transformerHfHz: 45000,
    outputImpedanceOhms: 0.8,
    feedbackDb: 12,
    crosstalkDb: -70,
    nonlinear: { kind: 'pentode', amount: 0.55, asymmetry: 0.26, bias: 0.2, headroomDb: 11, oversample: 4 },
    dynamics: { sagDb: 0.55, sagTimeMs: 120, recoveryMs: 300, slewRateVus: 22, headroomDb: 1.4 },
    clipping: { kind: 'soft', kneeDb: 6, stickiness: 0.25 },
    confidence: 'ESTIMATED',
    accent: '#e0a458',
    tags: ['kt120', 'regulated-supply', 'low-sag'],
    notes:
      'Push-pull operation cancels even-order products in the output stage, so the residue is third-harmonic dominant -- the structural opposite of the single-ended entries here -- and the output transformer bounds the passband at both ends. Two things separate this profile from the other push-pull integrated in the group: about 12 dB of feedback, which drops the source impedance to 0.8 ohm and keeps the speaker impedance curve mostly out of the response, and a regulated, high-reserve supply, modelled as only 0.55 dB of sag on sustained full power where a conventionally supplied valve amplifier would give up more than a decibel.',
    provenanceNote:
      'The KT120 push-pull output, regulated screen supplies and the electronic bias and protection system come from Octave published material. Power, THD, signal-to-noise, bandwidth, output impedance and feedback factor are estimates typical of this design rather than confirmed specifications, so the entry is tagged ESTIMATED.',
  }),

  makeAmp({
    mfr: 'Einstein Audio',
    model: 'The Final Cut',
    price: usd(40000, 'APPROXIMATE', 2020, 'Price is for the monoblock pair; European list converted and rounded, treated as approximate.'),
    year: 1998,
    country: 'Germany',
    topology: 'tube-otl',
    form: 'monoblock',
    devices: 'Parallel 6C33C-B power triodes, output-transformerless, direct-coupled to the loudspeaker terminals',
    power: { watts8: 30, watts4: 20, confidence: 'ESTIMATED' },
    gainDb: 26,
    thdPercent: 0.8,
    snrDb: 88,
    bandwidth: [6, 70000],
    outputTransformer: false,
    outputImpedanceOhms: 2.0,
    feedbackDb: 6,
    crosstalkDb: -62,
    harmonicShape: HARMONIC_SHAPES.otl,
    nonlinear: { kind: 'triode', amount: 0.72, asymmetry: 0.5, bias: 0.5, headroomDb: 9, oversample: 8 },
    dynamics: { sagDb: 1.4, sagTimeMs: 200, recoveryMs: 450, slewRateVus: 20, headroomDb: 1.1 },
    clipping: { kind: 'soft', kneeDb: 8, stickiness: 0.32 },
    confidence: 'ESTIMATED',
    accent: '#c07a3e',
    tags: ['otl', '6c33c', 'load-sensitive', 'zero-transformer'],
    notes:
      'With no output transformer there is no primary inductance to set a low-frequency corner, no leakage inductance to set a high-frequency one and no core to saturate, which is why this profile is the widest-bandwidth valve entry in the group. The price is paid in source impedance: about 2 ohms, so a loudspeaker whose impedance swings from 4 to 12 ohms will have roughly 2 dB of its own impedance curve added to the response, and power falls rather than rises into 4 ohms. Distortion is third-harmonic led with a substantial second-harmonic component, the OTL signature, and rises steeply as the tubes run out of current.',
    provenanceNote:
      'The output-transformerless topology and the 6C33C-B output triodes come from Einstein published material. Power, THD, signal-to-noise, bandwidth, output impedance and feedback are estimates typical of a low-feedback OTL; Einstein does not publish a full specification set, so the entry is tagged ESTIMATED.',
  }),

  makeAmp({
    mfr: 'Trafomatic Audio',
    model: 'Head 2',
    price: usd(2000, 'APPROXIMATE', 2019, 'European retail has been quoted around 1,800-2,200 USD equivalent; treated as approximate.'),
    year: 2013,
    country: 'Serbia',
    topology: 'tube-set',
    form: 'headphone',
    devices: 'Single-ended valve gain stage into in-house output transformers, no global feedback',
    power: { mw32: 350, mw300: 900, mw600: 700, confidence: 'ESTIMATED' },
    gainDb: 20,
    thdPercent: 0.6,
    snrDb: 90,
    bandwidth: [15, 40000],
    outputTransformer: true,
    transformerLfHz: 20,
    transformerHfHz: 36000,
    outputImpedanceOhms: 12,
    feedbackDb: 0,
    crosstalkDb: -60,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.8, asymmetry: 0.75, bias: 0.8, headroomDb: 9, oversample: 8 },
    dynamics: { sagDb: 1.0, sagTimeMs: 160, recoveryMs: 380, slewRateVus: 16, headroomDb: 1.2 },
    clipping: { kind: 'soft', kneeDb: 8.5, stickiness: 0.36 },
    confidence: 'ESTIMATED',
    accent: '#8f7a6a',
    tags: ['headphone', 'transformer-coupled', 'zero-feedback'],
    notes:
      'A zero-feedback single-ended stage working into an output transformer, so the second harmonic dominates and grows roughly 1 dB per dB of drive rather than staying at a fixed floor. The number that changes the sound of a headphone on this amplifier is the 12 ohm source impedance: a dynamic headphone with a 30 ohm impedance peak at its bass resonance sitting on a 32 ohm nominal load will show around 2 dB of lift at that peak, while a flat-impedance planar sees none. Power delivery peaks into 300 ohms because the transformer ratio suits high-impedance loads, which is the reverse of a solid-state headphone amplifier.',
    provenanceNote:
      'Trafomatic is an output-transformer manufacturer and this model uses in-house magnetics with a valve gain stage; that much comes from Trafomatic published material. The single-ended classification, the power figures into 32, 300 and 600 ohms, THD, bandwidth, source impedance and signal-to-noise are all estimates typical of a transformer-coupled valve headphone stage, so the entry is tagged ESTIMATED. No THD figure is published for this model.',
  }),

  makeAmp({
    mfr: 'Unison Research',
    model: 'Unico 150',
    price: usd(5500, 'APPROXIMATE', 2020, 'US retail has been quoted in the 5,000-6,000 USD range; treated as approximate.'),
    year: 2015,
    country: 'Italy',
    topology: 'hybrid',
    form: 'integrated',
    devices: 'Double-triode voltage gain stage direct-coupled into a complementary MOSFET output bank',
    power: { watts8: 160, watts4: 250, confidence: 'ESTIMATED' },
    gainDb: 36,
    thdPercent: 0.1,
    snrDb: 96,
    bandwidth: [8, 80000],
    outputImpedanceOhms: 0.12,
    feedbackDb: 16,
    crosstalkDb: -80,
    nonlinear: { kind: 'triode', amount: 0.46, asymmetry: 0.5, bias: 0.62, headroomDb: 13, oversample: 4 },
    dynamics: { sagDb: 0.45, sagTimeMs: 70, recoveryMs: 180, slewRateVus: 40, headroomDb: 1.7 },
    clipping: { kind: 'asymmetric', kneeDb: 4.0, stickiness: 0.14 },
    confidence: 'ESTIMATED',
    accent: '#7d9b76',
    tags: ['valve-input', 'mosfet-output', 'split-signature'],
    notes:
      'The division of labour is the whole point: the valve supplies nearly all of the voltage gain, so the harmonic signature is set by a triode and is second-harmonic led and level-dependent, while the MOSFET bank supplies the current and therefore sets the damping. The result is a profile that behaves like a valve amplifier in the distortion domain and like a solid-state amplifier in the load domain -- a 0.12 ohm source impedance, two orders of magnitude below the OTL entry in this group, so the loudspeaker impedance curve does not appear in the response at all. MOSFET output devices also make the clipping onset softer than a bipolar bank of the same rating.',
    provenanceNote:
      'The valve-input, MOSFET-output hybrid topology comes from Unison Research published material. Power, THD, signal-to-noise, bandwidth, output impedance and feedback factor are estimates rather than confirmed specifications, so the entry is tagged ESTIMATED.',
  }),

  makeAmp({
    mfr: 'Synthesis',
    model: 'Roma 96DC',
    price: usd(4000, 'APPROXIMATE', 2020, 'European retail has been quoted near 3,500-4,500 USD equivalent; treated as approximate.'),
    year: 2018,
    country: 'Italy',
    topology: 'tube-push-pull',
    form: 'integrated',
    devices: 'EL34 push-pull output stage with a double-triode front end',
    power: { watts8: 50, watts4: 50, confidence: 'ESTIMATED' },
    gainDb: 34,
    thdPercent: 0.5,
    snrDb: 88,
    bandwidth: [18, 32000],
    outputTransformer: true,
    transformerLfHz: 24,
    transformerHfHz: 28000,
    outputImpedanceOhms: 2.6,
    feedbackDb: 4,
    crosstalkDb: -58,
    nonlinear: { kind: 'pentode', amount: 0.72, asymmetry: 0.38, bias: 0.35, headroomDb: 8, oversample: 8 },
    dynamics: { sagDb: 1.5, sagTimeMs: 210, recoveryMs: 500, slewRateVus: 15, headroomDb: 0.9 },
    clipping: { kind: 'soft', kneeDb: 8, stickiness: 0.38 },
    confidence: 'ESTIMATED',
    accent: '#b05c5c',
    tags: ['el34', 'low-feedback', 'narrow-bandwidth'],
    notes:
      'Nominally the same topology as the Octave entry and deliberately the opposite execution: about 4 dB of feedback instead of 12, a 2.6 ohm source impedance instead of 0.8, and magnetics with corners at 24 Hz and 28 kHz instead of 14 Hz and 45 kHz. At that source impedance a loudspeaker dipping to 4 ohms loses close to 1.5 dB relative to its 8 ohm regions, so the response of the pair is jointly determined rather than being a property of either. Core saturation lifts low-order distortion below about 50 Hz near full power, and supply sag of roughly 1.5 dB softens sustained peaks well before the clipping point.',
    provenanceNote:
      'The EL34 push-pull output stage comes from Synthesis published material. Power, THD, signal-to-noise, bandwidth, transformer corner frequencies, output impedance and feedback factor are all estimates typical of a low-feedback EL34 integrated; Synthesis publishes limited numerical data, so the entry is tagged ESTIMATED and no figure here is presented as a manufacturer specification.',
  }),
];
